import clsx from 'clsx'
import first from 'lodash/first'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgCheck from '@Assets/SVG/Check'
import SvgPlus from '@Assets/SVG/Plus'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { Wordbook } from '@Components/Wordbook'
import { Button } from '@CoreComponents/Buttons/Button'
import { SuccessPromptProps } from '@Domains/Cart/Components/Prompt/Successv2/SuccessPrompt'
import { SideCartContext } from '@Domains/Cart/Context/SideCartContextProvider'
import { LineItem } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/LineItem'
import { FreeShippingCountdown } from '@Domains/Cart/Feature/FreeShippingCountdown/FreeShippingCountdown'
import { CartMilestonesV2 } from '@Domains/Cart/Feature/SideCart/Components/CartMilestones/CartMilestonesV2'
import { ProductItem } from '@Domains/Product/Components/ProductItem'
import { LegacyProductTacticsDoNotUse } from '@Domains/Product/Features/Tactics/LegacyProductTacticsDoNotUse'
import { CartQuery, SplitTestGroup } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { Test } from '@Globals/SplitTests/types'
import { useTranslation } from '@Globals/Translations'
import { giftWrappingIDs } from '@Shared/Statics/GiftWrappings'
import { createAddToCartTacticFromOffer } from '@Shared/Utilities/AddToCartHelper'

import styles from './SuccessPromptV3.module.scss'

type SuccessPromptV3Props = SuccessPromptProps & {
  data: CartQuery | undefined
  giftId?: string

}

export const SuccessPromptV3: FC<SuccessPromptV3Props> = ({
  data,
  giftId: GIFT_ID,
  onClose,
  onModalToggle,
  origins,
  primaryLineItemID,
  recommendations,
  tactic,

}) => {
  const [matchingVariantProductSuccess, setMatchingVariantProductSuccess] = useState<boolean>(false)
  const [addedIds, setAddedIds] = useState<string[]>([])
  const { t } = useTranslation()

  const [lineItemGroup, lineItemsInGroup] = useMemo(() => {
    if (primaryLineItemID) {
      const group = data?.cart?.lineItemGroups?.find(g => g.lineItemIDs?.includes(primaryLineItemID))
      if (!group) {
        return [null, null]
      }
      return [group, data?.cart?.lineItems?.filter(lineItem => group.lineItemIDs?.includes(lineItem.id))]
    }
    return [null, null]
  }, [data?.cart?.lineItemGroups, data?.cart?.lineItems, primaryLineItemID])

  useEffect(() => {
    ampli.promptOpen({ source_id: 'success_prompt' })
  }, [])

  const lineItems = useMemo(() => data?.cart?.lineItems || [], [data?.cart?.lineItems])
  const lineItem = useMemo(() => {
    const firstOrigin = first(origins)
    // IF GIFT_CARD GET THE ONE WITH SELECTED AMOUNT
    if (firstOrigin && firstOrigin.productID === GIFT_ID && firstOrigin?.giftcard?.amount) {
      const giftAmount = firstOrigin.giftcard.amount
      return lineItems?.find(_lineItem => _lineItem.productID === GIFT_ID && `${_lineItem.totalPrice?.plain || 0}`.replace(/^\D+/g, '') === `${giftAmount}`)
            || lineItems?.find(_lineItem => _lineItem.id === primaryLineItemID)
    }
    return lineItems?.find(_lineItem => _lineItem.id === primaryLineItemID)
  }, [origins, GIFT_ID, lineItems, primaryLineItemID])
  const personalizationsLinkedToLineItem = useMemo(() => lineItems?.filter(
    l => l.type?.__typename === 'CartLineItemTypeUpsellPersonalization' && l.type?.parent === lineItem?.type?.link,
  ), [lineItem?.type?.link, lineItems])
  const { storeURL } = useContext(MetadataContext)
  const { openCart } = useContext(SideCartContext)

  const handleOpenCart = () => {
    openCart()
    onClose()
  }

  const hasMatchingVariant = useMemo(
    () => !!tactic,
    [tactic],
  )

  const additionProduct = useMemo(() => {
    const items = first(recommendations)?.products
    const giftWrapping = lineItem?.variant?.giftWrappings?.__typename === 'ProductAttribute' && first(lineItem.variant?.giftWrappings?.products)
    const products = items?.filter(p => (p.type !== 'personalizations' && p.type !== 'packagings')) || undefined
    if (products?.length === 1) {
      return giftWrapping ? products.filter(p => p.id !== giftWrapping.id) : products
    }
    return undefined
  }, [lineItem, recommendations])

  const haveMilestones = useMemo(() => [
    !isEmpty(data?.cart?.milestones?.steps),
    data?.cart?.milestones?.steps?.length === 1 && first(data?.cart?.milestones?.steps)?.type === 'FREE_SHIPPING',
  ], [data?.cart?.milestones?.steps])

  return (
    <>
      <NativeModalHeader className={styles.modalHeader}>
        {t('cart:ADDED_TO_CART')}
      </NativeModalHeader>
      <NativeModalBody className={clsx(styles.modalBody)}>

        {lineItemGroup && (
          <>
            <div className={styles.groupHeader}>
              <div className={styles.title}>{t('cart:BUNDLE_DISCOUNT_LABEL')}</div>
              <div className={styles.prices}>
                {lineItemGroup.totalPrice?.discount?.format && <span className={clsx(styles.discount)}>{lineItemGroup.totalPrice?.discount?.format}</span>}
                <span className={clsx(styles.price, lineItemGroup.totalPrice?.discount?.format && styles.discounted)}>{lineItemGroup.totalPrice?.format}</span>
              </div>
            </div>
            <div className={styles.lineItemGroup}>
              {lineItemsInGroup && map(lineItemsInGroup, groupedLineItem => (
                <LineItem
                  key={`${lineItemGroup.id}_${groupedLineItem.id}`}
                  additionAddedProducts={origins?.map(a => a.productID)}
                  additionProducts={additionProduct}
                  className={clsx(styles.lineItemInGroup, hasMatchingVariant && styles.lineItemWithMatchingVariant)}
                  giftWrappingIsAssociated={!!origins?.find(o => giftWrappingIDs.includes(o.productID))}
                  groupItem
                  isPowerStep
                  lineItem={groupedLineItem}
                  onModalToggle={onModalToggle}
                  personalizationsLinkedToLineItem={hasMatchingVariant ? [] : personalizationsLinkedToLineItem}
                  showAdditionProducts={false}
                  showPersonalizationModifiers={false}
                  showPersonalizeHeader={false}
                  showPersonalizeImagesOnAddedProducts={false}
                  showPersonalizeImagesOnUnaddedProducts={false}
                  showQuantity={false}
                  showQuantityBubble={false}
                  showQuantityButtons={false}
                  showUpsell={false}
                  v2
                />
              ))}
            </div>
          </>
        )}
        {!lineItemsInGroup && lineItem
            && (
              <div className={styles.lineItem}>
                <LineItem
                  additionAddedProducts={origins?.map(a => a.productID)}
                  additionProducts={additionProduct}
                  className={clsx(hasMatchingVariant && styles.lineItemWithMatchingVariant)}
                  giftWrappingIsAssociated={!!origins?.find(o => giftWrappingIDs.includes(o.productID))}
                  isPowerStep
                  lineItem={lineItem}
                  onModalToggle={onModalToggle}
                  personalizationsLinkedToLineItem={hasMatchingVariant ? [] : personalizationsLinkedToLineItem}
                  showAdditionProducts={!hasMatchingVariant}
                  showPersonalizationModifiers
                  showPersonalizeHeader={!hasMatchingVariant}
                  showPersonalizeImagesOnAddedProducts={false}
                  showPersonalizeImagesOnUnaddedProducts
                  showQuantity={false}
                  showQuantityButtons={false}
                  showUpsell={!hasMatchingVariant}
                  v2
                />
              </div>
            )}
        {!haveMilestones && (
          <FreeShippingCountdown
            expectedDelivery={data?.cart?.expectedDelivery || ''}
            isFree={!!data?.cart?.freeShipping?.isFree}
            remaining={data?.cart?.freeShipping?.remaining || undefined}
            showFreeShippingProgress
            v2
          />
        )}
        {haveMilestones && <CartMilestonesV2 cart={data?.cart} className="fullwidth" />}
        {hasMatchingVariant
            && lineItem
            && tactic?.__typename === 'TacticMatchingVariant'
            && tactic?.productID && (
              <div className={clsx(styles.matchingVariantWrapper, 'fullwidth')}>
                <div className={styles.matchingVariantTitle}><Wordbook wordbook={tactic.title} /></div>
                <ProductItem
                  action={(
                    <BuyButtonV2
                      className={clsx(styles.AddToCartProduct, matchingVariantProductSuccess && styles.success)}
                      color="black"
                      disabled={matchingVariantProductSuccess}
                      onSuccess={() => setMatchingVariantProductSuccess(true)}
                      productID={tactic.productID}
                      products={[
                        {
                          quantity: 1,
                          variantID: '',
                          productID: tactic.productID,
                          ...tactic.offer && tactic.offer.price && {
                            parentLineItemID: lineItem.id,
                            tactic: createAddToCartTacticFromOffer(tactic.offer),
                          },
                        },
                      ]}
                      sourceID="matching_variant_products"
                      useCache={false}
                      withoutSuccessPrompt
                    >
                      {!matchingVariantProductSuccess && <SvgPlus />}
                      {matchingVariantProductSuccess && <SvgCheck />}
                    </BuyButtonV2>
                  )}
                  analytics={{ source_id: 'add_to_cart_bought_together' }}
                  className={styles.matchingVariantProduct}
                  content={tactic.product?.name}
                  discount={tactic.offer?.price?.discount?.format || tactic.product?.masterVariant?.price?.discount?.format || undefined}
                  imageUrl={tactic.product?.primaryAsset?.url || ''}
                  price={tactic.offer?.price?.format || tactic.product?.masterVariant?.price?.format || ''}
                />
              </div>
        )}
        {lineItem && tactic && (
          <LegacyProductTacticsDoNotUse
            relatedLineItem={lineItem}
            // TODO: Await Mads if we are doing backend so we do not need to show progress bar at all times -- showProgressBar={!haveMilestones}
            showProgressBar
            tactics={[tactic]}
          />
        )}

        {map(recommendations, (recommendation) => {
          const filteredRecommendation = recommendation.products?.filter(p => p.type !== 'personalizations'
              && p.type !== 'packagings'
              && !giftWrappingIDs.includes(p.id)
              && !!p.availability?.isInStock)

          if (isEmpty(filteredRecommendation) || filteredRecommendation!.length < 2) {
            return null
          }

          let component = (
            <div key={recommendation.id} className={styles.recommendations}>
              <div className={styles.recommendationsTitle}>{recommendation.title}</div>
              {map(filteredRecommendation, product => (
                <ProductItem
                  key={product.id}
                  action={(
                    <BuyButtonV2
                      className={clsx(styles.AddToCartProduct, addedIds.includes(product.id) && styles.success)}
                      color="black"
                      onSuccess={() => {
                        setAddedIds(prev => [...prev, product.id])
                      }}
                      preventDefault
                      productID={product.id}
                      products={[{ quantity: 1, variantID: '', productID: product.id }]}
                      sourceID={`add_to_cart_${recommendation.id}`}
                      stopPropagation
                      useCache={false}
                      withoutSuccessPrompt
                    >
                      {!addedIds.includes(product.id) ? <SvgPlus /> : <SvgCheck />}
                    </BuyButtonV2>
                    )}
                  analytics={{ source_id: 'add_to_cart_bought_together' }}
                  className={styles.recommendationsProduct}
                  content={product.name}
                  discount={product.masterVariant?.price?.discount?.format || ''}
                  imageUrl={product.primaryAsset?.url || ''}
                  price={product.masterVariant?.price?.format || ''}
                  url={storeURL(product.url || '')}
                />
              ))}
            </div>
          )

          const { splitTest } = recommendation

          if (splitTest?.test) {
            component = (
              <Experiment
                key={recommendation.id}
                a={includes(splitTest.groups, SplitTestGroup.A) ? component : undefined}
                b={includes(splitTest.groups, SplitTestGroup.B) ? component : undefined}
                c={includes(splitTest.groups, SplitTestGroup.C) ? component : undefined}
                d={includes(splitTest.groups, SplitTestGroup.D) ? component : undefined}
                test={splitTest.test as Test}
              />
            )
          }

          return component
        })}

      </NativeModalBody>
      <NativeModalFooter className={styles.modalFooter}>
        <Button className={styles.button} innerSpacing="medium" onClick={handleOpenCart} theme="black">
          {t('GO_TO_CART')}
        </Button>
        <Button
          className={clsx(styles.button, styles.continueButton)}
          innerSpacing="medium"
          onClick={() => onClose()}
          theme="white"
          type="button"
        >
          {t('cart:PP_CONTINUE_SHOPPING_BUTTON_TEXT')}
        </Button>
      </NativeModalFooter>
    </>
  )
}
